// ── Context A: Owner creation gate (trip-level) ──
// Used ONLY for POST /api/trips and POST /api/auth/verify-owner.
// NOT persisted beyond the immediate request.

/**
 * Verify owner password and return the token for a single request.
 * Does NOT persist the token anywhere — caller uses it inline.
 */
export async function verifyOwnerPassword(password) {
  // This is called from OwnerPasswordDialog — the token is returned
  // to the caller, who attaches it to one request and discards it.
  // Import is deferred to avoid circular dependency with api.js.
  const { default: api } = await import('./api');
  const { data } = await api.post('/auth/verify-owner', { password: password.trim() });
  if (!data.gate_active) return '__disabled__';
  return data.token;
}

// ── Context B: Per-round scored-round unlock (session-scoped) ──
// Cached in sessionStorage, keyed per trip+round.
// Clears on tab close.

function _roundKey(tripCode, roundId) {
  return `foretour:roundUnlock:${tripCode.toUpperCase()}:${roundId}`;
}

export function getRoundUnlockToken(tripCode, roundId) {
  try { return sessionStorage.getItem(_roundKey(tripCode, roundId)) || null; }
  catch { return null; }
}

export function setRoundUnlockToken(tripCode, roundId, token) {
  try { sessionStorage.setItem(_roundKey(tripCode, roundId), token); }
  catch { /* sessionStorage unavailable */ }
}

export function isRoundUnlocked(tripCode, roundId) {
  return !!getRoundUnlockToken(tripCode, roundId);
}

export function clearRoundUnlockToken(tripCode, roundId) {
  try { sessionStorage.removeItem(_roundKey(tripCode, roundId)); }
  catch {}
}

/**
 * Check if ANY round unlock exists for a given trip.
 * Used by UI components that need a broad "is anything unlocked" check.
 */
export function hasAnyRoundUnlock(tripCode) {
  try {
    const prefix = `foretour:roundUnlock:${tripCode.toUpperCase()}:`;
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(prefix)) return true;
    }
  } catch {}
  return false;
}

// ── Context C: Per-round score-edit unlock for revealed rounds (session-scoped) ──
// Separate from Context B (structural unlock). Both can coexist for the same round.

function _scoreEditKey(tripCode, roundId) {
  return `foretour:scoreEditUnlock:${tripCode.toUpperCase()}:${roundId}`;
}

export function getScoreEditUnlockToken(tripCode, roundId) {
  try { return sessionStorage.getItem(_scoreEditKey(tripCode, roundId)) || null; }
  catch { return null; }
}

export function setScoreEditUnlockToken(tripCode, roundId, token) {
  try { sessionStorage.setItem(_scoreEditKey(tripCode, roundId), token); }
  catch {}
}

export function hasScoreEditUnlock(tripCode, roundId) {
  return !!getScoreEditUnlockToken(tripCode, roundId);
}

// ── Legacy cleanup ──
// Remove old persistent owner tokens that leaked across contexts.
export function clearLegacyOwnerTokens() {
  try {
    localStorage.removeItem('foretour:ownerUnlocked');
    document.cookie = 'foretour_owner_token=;path=/;max-age=0';
  } catch {}
}

// Backward-compat shim — DEPRECATED, returns false always.
// Old call sites that check isOwnerUnlocked() now get false,
// forcing the proper per-round unlock flow.
export function isOwnerUnlocked() { return false; }
export function getOwnerToken() { return null; }
export function setOwnerToken() { /* no-op */ }
export function clearOwnerToken() { clearLegacyOwnerTokens(); }


// ── Group-claim token helpers ──
function _groupClaimKey(tripCode, roundId) {
  return `foretour:groupClaim:${tripCode}:${roundId}`;
}
export function getGroupClaimToken(tripCode, roundId) {
  try {
    const raw = localStorage.getItem(_groupClaimKey(tripCode, roundId));
    return raw ? JSON.parse(raw)?.token || null : null;
  } catch { return null; }
}
export function getGroupClaimData(tripCode, roundId) {
  try {
    const raw = localStorage.getItem(_groupClaimKey(tripCode, roundId));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
export function setGroupClaimData(tripCode, roundId, data) {
  try { localStorage.setItem(_groupClaimKey(tripCode, roundId), JSON.stringify(data)); } catch {}
}
export function clearGroupClaim(tripCode, roundId) {
  try { localStorage.removeItem(_groupClaimKey(tripCode, roundId)); } catch {}
}

// ── Device ID: unique per browser/storage (persists across sessions) ──
const _DEVICE_ID_KEY = 'foretour:device_id';
export function getDeviceId() {
  try {
    let id = localStorage.getItem(_DEVICE_ID_KEY);
    if (!id) {
      id = 'dev_' + crypto.randomUUID();
      localStorage.setItem(_DEVICE_ID_KEY, id);
    }
    return id;
  } catch { return 'dev_' + Math.random().toString(36).slice(2); }
}
